'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface CalculatorResult {
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  discountPercentage: number;
}

const SavingsCalculator = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [spendingAmount, setSpendingAmount] = useState<string>('');
  const [selectedCard, setSelectedCard] = useState<'vip' | 'family'>('vip');
  const [result, setResult] = useState<CalculatorResult | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const calculateSavings = () => {
    const amount = parseFloat(spendingAmount);
    if (isNaN(amount) || amount <= 0) {
      return;
    }

    const discountPercentage = selectedCard === 'vip' ? 0.35 : 0.25;
    const discountAmount = amount * discountPercentage;
    const finalAmount = amount - discountAmount;

    setResult({
      originalAmount: amount,
      discountAmount,
      finalAmount,
      discountPercentage: discountPercentage * 100
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setSpendingAmount(value);
    setResult(null);
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-primary/90 border-t border-accent/20">
        <div className="container mx-auto px-6">
          <div className="bg-primary-foreground/10 rounded-2xl shadow-lg p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-white/20 rounded w-1/3"></div>
              <div className="h-4 bg-white/20 rounded w-2/3"></div>
              <div className="h-12 bg-white/20 rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-primary/90 border-t border-accent/20">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white font-amiri mb-4">
              {t('احسب توفيرك الشهري', 'Calculate Your Monthly Savings')}
            </h2>
            <p className="text-lg text-white/70">
              {t('اكتشف كم ستوفر مع بطاقات الاشتراك الخاصة بنا', 'Discover how much you will save with our subscription cards')}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-8 space-y-8 border border-accent/30">
            {/* Card Type Selection */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-3">
                {t('اختر نوع البطاقة', 'Choose Card Type')}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSelectedCard('vip')}
                  className={`p-6 rounded-xl border-2 transition-premium ${
                    selectedCard === 'vip' ? 'border-accent bg-accent/20' : 'border-white/20 hover:border-accent/50 bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <Icon name="StarIcon" size={24} className="text-accent" />
                    <span className="text-xl font-bold font-amiri text-white">{t('بطاقة VIP', 'VIP Card')}</span>
                  </div>
                  <p className="text-sm text-white/60">{t('خصم حتى 35%', 'Discount up to 35%')}</p>
                </button>

                <button
                  onClick={() => setSelectedCard('family')}
                  className={`p-6 rounded-xl border-2 transition-premium ${
                    selectedCard === 'family' ? 'border-accent bg-accent/20' : 'border-white/20 hover:border-accent/50 bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <Icon name="UserGroupIcon" size={24} className="text-accent" />
                    <span className="text-xl font-bold font-amiri text-white">{t('بطاقة العائلة', 'Family Card')}</span>
                  </div>
                  <p className="text-sm text-white/60">{t('خصم حتى 25%', 'Discount up to 25%')}</p>
                </button>
              </div>
            </div>

            {/* Amount Input */}
            <div>
              <label htmlFor="spending-amount" className="block text-sm font-medium text-white/90 mb-3">
                {t('أدخل مبلغ الإنفاق الشهري (ريال سعودي)', 'Enter Monthly Spending Amount (SAR)')}
              </label>
              <div className="relative">
                <input
                  id="spending-amount"
                  type="text"
                  value={spendingAmount}
                  onChange={handleInputChange}
                  placeholder={t('مثال: 5000', 'Example: 5000')}
                  className="w-full px-6 py-4 pr-12 rounded-xl border-2 border-white/20 bg-white/10 text-white placeholder-white/40 focus:border-accent focus:outline-none transition-premium text-lg"
                />
                <Icon
                  name="BanknotesIcon"
                  size={24}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50"
                />
              </div>
            </div>

            {/* Calculate Button */}
            <button
              onClick={calculateSavings}
              disabled={!spendingAmount}
              className="w-full py-4 bg-accent text-white rounded-xl font-bold text-lg transition-premium hover:bg-accent/90 hover:shadow-lg active-press disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <Icon name="CalculatorIcon" size={24} />
              <span>{t('احسب التوفير', 'Calculate Savings')}</span>
            </button>

            {/* Results */}
            {result && (
              <div className="bg-accent/20 rounded-xl p-8 space-y-6 border-2 border-accent/40">
                <h3 className="text-2xl font-bold text-white font-amiri text-center mb-6">
                  {t('نتائج الحساب', 'Calculation Results')}
                </h3>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-white/60 mb-2">{t('المبلغ الأصلي', 'Original Amount')}</p>
                    <p className="text-2xl font-bold text-white data-text">
                      {formatNumber(result.originalAmount)} {t('ريال', 'SAR')}
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-white/60 mb-2">{t('قيمة الخصم', 'Discount Value')}</p>
                    <p className="text-2xl font-bold text-accent data-text">
                      {formatNumber(result.discountAmount)} {t('ريال', 'SAR')}
                    </p>
                    <p className="text-xs text-accent/80 font-medium mt-1">
                      ({result.discountPercentage}% {t('خصم', 'discount')})
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-white/60 mb-2">{t('المبلغ النهائي', 'Final Amount')}</p>
                    <p className="text-2xl font-bold text-white data-text">
                      {formatNumber(result.finalAmount)} {t('ريال', 'SAR')}
                    </p>
                  </div>
                </div>

                <div className="bg-accent/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-white">
                    <Icon name="SparklesIcon" size={20} className="inline ml-2 text-accent" />
                    {t('ستوفر', 'You will save')} <span className="font-bold text-accent data-text">{formatNumber(result.discountAmount)} {t('ريال', 'SAR')}</span> {t('شهرياً مع بطاقة', 'monthly with the')} {selectedCard === 'vip' ? 'VIP' : t('العائلة', 'Family')} {t('!', ' card!')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator;